import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Image as ImageIcon, FileText, Sparkles } from 'lucide-react';
import { openUpgradeModal } from './UpgradeModal';

/**
 * Compact usage meter shown in the dashboard sidebar. Polls /api/me/usage
 * and renders two slim progress bars: AI images + posts. Clicking the widget
 * opens the upgrade modal — useful when users are close to hitting their cap.
 *
 * Legacy enterprise users (unlimited) see a simple "Unlimited" badge instead
 * of bars so they aren't shown irrelevant percentages.
 */
const REFRESH_MS = 30_000; // lightweight — once every 30s keeps the meter accurate after image/post generation

export const UsageMeter = () => {
  const { api, user } = useAuth();
  const [usage, setUsage] = useState(null);

  const fetchUsage = useCallback(async () => {
    try {
      const r = await api.get('/me/usage');
      setUsage(r.data);
    } catch (_) { /* fail-soft — don't surface 4xx to the sidebar */ }
  }, [api]);

  useEffect(() => {
    if (!user) return;
    fetchUsage();
    const iv = setInterval(fetchUsage, REFRESH_MS);
    return () => clearInterval(iv);
  }, [user, fetchUsage]);

  if (!usage) return null;

  const images = usage.images || {};
  const posts = usage.posts || {};
  const isUnlimited = images.limit === -1 && posts.limit === -1;

  if (isUnlimited) {
    return (
      <div
        className="px-3 py-2 mb-1 rounded-lg bg-gradient-to-br from-indigo-50 to-violet-50 border border-indigo-100"
        data-testid="usage-meter"
      >
        <div className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-indigo-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-semibold text-indigo-700 leading-tight truncate">
              {usage.tier_name || 'Unlimited'}
            </p>
            <p className="text-[10px] text-indigo-500 leading-tight">
              Unlimited images + posts
            </p>
          </div>
        </div>
      </div>
    );
  }

  const imgPct = images.limit > 0 ? Math.min(100, (images.used / images.limit) * 100) : 0;
  const postPct = posts.limit > 0 ? Math.min(100, (posts.used / posts.limit) * 100) : 0;
  const lowImages = images.remaining !== -1 && images.remaining <= Math.max(1, Math.floor(images.limit * 0.2));
  const lowPosts = posts.remaining !== -1 && posts.remaining <= Math.max(1, Math.floor(posts.limit * 0.2));
  const exhausted = images.remaining === 0 || posts.remaining === 0;

  return (
    <button
      type="button"
      onClick={() => openUpgradeModal(`You've used ${images.used}/${images.limit} AI images this cycle`)}
      className={`w-full text-left px-3 py-2.5 mb-1 rounded-lg transition-colors border ${
        exhausted
          ? 'bg-red-50 border-red-200 hover:bg-red-100'
          : lowImages || lowPosts
          ? 'bg-amber-50 border-amber-200 hover:bg-amber-100'
          : 'bg-zinc-50 border-zinc-100 hover:bg-zinc-100'
      }`}
      data-testid="usage-meter"
      aria-label="View plan usage"
    >
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
          {usage.tier_name || 'Your plan'} {usage.is_trial && <span className="text-indigo-600">(trial)</span>}
        </p>
        {exhausted && (
          <span className="text-[10px] font-semibold text-red-600">Upgrade →</span>
        )}
      </div>
      <UsageBar
        icon={ImageIcon}
        label="AI images"
        used={images.used}
        limit={images.limit}
        pct={imgPct}
        exhausted={images.remaining === 0}
        low={lowImages}
      />
      <div className="h-1.5" />
      <UsageBar
        icon={FileText}
        label="Posts"
        used={posts.used}
        limit={posts.limit}
        pct={postPct}
        exhausted={posts.remaining === 0}
        low={lowPosts}
      />
    </button>
  );
};

const UsageBar = ({ icon: Icon, label, used, limit, pct, exhausted, low }) => (
  <div>
    <div className="flex items-center justify-between text-[11px] mb-1 leading-tight">
      <span className="flex items-center gap-1.5 text-zinc-600 font-medium">
        <Icon className="w-3 h-3" />
        {label}
      </span>
      <span className={`tabular-nums font-semibold ${exhausted ? 'text-red-600' : low ? 'text-amber-700' : 'text-zinc-700'}`}>
        {used}/{limit}
      </span>
    </div>
    <div className="h-1 rounded-full bg-white/70 overflow-hidden">
      <div
        className={`h-full transition-all duration-500 ${
          exhausted ? 'bg-red-500' : low ? 'bg-amber-500' : 'bg-indigo-600'
        }`}
        style={{ width: `${pct}%` }}
      />
    </div>
  </div>
);

export default UsageMeter;
